import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Input, Stack } from "@mui/joy";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import LinkV2 from "components/common/LinkV2";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  type AmbassadorContractType,
  getAmbassadorToken,
} from "api/ambassadorApi";
import {
  formatPhoneNumberInput,
  isValidKrMobile,
  normalizePhoneNumber,
} from "utils/phoneNumberUtils";
import { CONTRACT_FILE_MAX_BYTES, readFileAsDataUrl } from "utils/fileUtils";
import distancingLogo from "static/image/common/distancing_logo.png";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const CONTRACT_TYPES: {
  value: AmbassadorContractType;
  label: string;
}[] = [
  { value: "freelancer", label: "프리랜서 (개인)" },
  { value: "business_general", label: "사업자 (일반과세)" },
  { value: "business_simplified", label: "사업자 (간이과세)" },
];

// 독립 웹 페이지 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

// 앰버서더 계약 정보 제출/수정 — 계약형태별 필수 파일·정보를 받아 운영이 계약서를 발송할 수 있게 한다.
// 프리랜서 = 통장사본 + 실명 / 사업자 = 통장사본 + 사업자등록증 + 사업자명. 제출 후에도 재진입해 수정 가능(프로필 관리).
// 파일은 base64 data URL 로 서버에 저장된다 (5MB 이하, fileUtils 참고).
export default function AmbassadorContract() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [contractType, setContractType] =
    useState<AmbassadorContractType>("freelancer");
  const [realName, setRealName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contractEmail, setContractEmail] = useState("");
  // 프리랜서: 로그인 인증 번호 고정 / 사업자: 연락 가능한 번호(유선 포함) 직접 입력
  const [loginPhoneNumber, setLoginPhoneNumber] = useState("");
  const [contractPhoneNumber, setContractPhoneNumber] = useState("");
  const [activityName, setActivityName] = useState("");
  const [bankbookFile, setBankbookFile] = useState<string | null>(null);
  const [businessRegistrationFile, setBusinessRegistrationFile] = useState<
    string | null
  >(null);
  // 업로드한 파일의 원본 파일명 — 서버에는 저장하지 않으므로 기존 제출분은 null (표시 폴백: "저장된 파일")
  const [bankbookFileName, setBankbookFileName] = useState<string | null>(null);
  const [businessRegistrationFileName, setBusinessRegistrationFileName] =
    useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // 계약 정보는 1회 제출 — 이미 제출된 경우 읽기 전용으로 보여준다
  const [submitted, setSubmitted] = useState(false);
  // 계약 완료 여부 + 체결된 계약서 사본 (어드민이 완료 처리 시 업로드) — 당사자 미리보기·다운로드용
  const [completed, setCompleted] = useState(false);
  const [contractFile, setContractFile] = useState<string | null>(null);
  // 제출 전 확인 팝업
  const [confirming, setConfirming] = useState(false);

  const hasToken = !!getAmbassadorToken();

  // 기존 제출값 로드 + 수신 번호는 로그인 전화번호로 프리필
  useEffect(() => {
    if (!hasToken) return;
    (async () => {
      try {
        const [contractRes, dashboardRes] = await Promise.all([
          ambassadorApi.getContract(),
          ambassadorApi.dashboard(),
        ]);
        if (contractRes.code === 0 && contractRes.result) {
          const c = contractRes.result;
          setSubmitted(!!c.type);
          if (c.type) setContractType(c.type);
          setRealName(c.realName ?? "");
          setBusinessName(c.businessName ?? "");
          setContractEmail(c.email ?? "");
          setActivityName(c.name ?? "");
          setBankbookFile(c.bankbookFile ?? null);
          setBusinessRegistrationFile(c.businessRegistrationFile ?? null);
          setCompleted(!!c.completed);
          setContractFile(c.contractFile ?? null);
          const loginPhone =
            dashboardRes.code === 0 ? dashboardRes.result?.phoneNumber : null;
          if (loginPhone)
            setLoginPhoneNumber(formatPhoneNumberInput(loginPhone));
          // 사업자용 연락처 프리필 — 기존 제출값 우선, 없으면 로그인 번호.
          // 유선 번호는 휴대폰용 3-4-4 포맷이 어긋나므로 휴대폰(01x)일 때만 하이픈 포맷을 적용한다.
          const phone = c.phoneNumber ?? loginPhone;
          if (phone)
            setContractPhoneNumber(
              phone.startsWith("01") ? formatPhoneNumberInput(phone) : phone
            );
        }
      } catch (e) {
        // 401 은 인터셉터가 로그인으로 보낸다 — 그 외는 빈 폼으로 시작
      } finally {
        setLoading(false);
      }
    })();
  }, [hasToken]);

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  const isBusiness = contractType !== "freelancer";
  const filesFilled = isBusiness
    ? !!bankbookFile && !!businessRegistrationFile
    : !!bankbookFile;
  const namesFilled = isBusiness
    ? businessName.trim().length > 0
    : realName.trim().length > 0;
  // 제출에 쓸 번호 — 프리랜서는 로그인 인증 번호 고정, 사업자는 입력한 연락처(유선 허용)
  const submitPhoneNumber = isBusiness ? contractPhoneNumber : loginPhoneNumber;
  const phoneValid = isBusiness
    ? /^0\d{8,10}$/.test(normalizePhoneNumber(contractPhoneNumber))
    : isValidKrMobile(loginPhoneNumber);
  const canSubmit =
    !submitting &&
    !loading &&
    !submitted &&
    namesFilled &&
    filesFilled &&
    contractEmail.trim().length > 0 &&
    phoneValid &&
    activityName.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ambassadorApi.submitContract({
        type: contractType,
        realName: isBusiness ? null : realName.trim(),
        businessName: isBusiness ? businessName.trim() : null,
        email: contractEmail.trim(),
        phoneNumber: normalizePhoneNumber(submitPhoneNumber),
        name: activityName.trim(),
        bankbookFile,
        businessRegistrationFile: isBusiness ? businessRegistrationFile : null,
      });
      if (res.code === 0) {
        setDone(true);
        return;
      }
      setConfirming(false);
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const contractTypeLabel =
    CONTRACT_TYPES.find(({ value }) => value === contractType)?.label ??
    contractType;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "480px",
          px: "20px",
          py: "40px",
        }}
      >
        {done ? (
          <Stack spacing={4} alignItems="center" sx={{ pt: "48px" }}>
            <img
              src={distancingLogo}
              alt="distancing logo"
              style={{ width: "138px", height: "auto" }}
            />
            <Stack spacing={"12px"} alignItems="center">
              <CustomTypography
                variant="H3"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"계약 정보가 저장됐어요"}
              </CustomTypography>
              <CustomTypography
                variant="Body"
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {"입력해 주신 이메일로 계약서를 보내드릴게요."}
              </CustomTypography>
            </Stack>
            <Button
              variant="Action"
              size="lg"
              onClick={() => navigate(PATH.ambassador)}
              sx={{
                width: "100%",
                maxWidth: "345px",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"홈으로 돌아가기"}
            </Button>
          </Stack>
        ) : (
          <>
            <Box sx={{ display: "flex" }}>
              <LinkV2
                variant="Caption"
                text="← 홈으로"
                onClick={() => navigate(PATH.ambassador)}
              />
            </Box>
            <Stack spacing={"8px"} sx={{ mt: 2 }}>
              <CustomTypography
                variant="H3"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"계약 정보"}
              </CustomTypography>
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {"계약서 발송과 정산에 필요한 정보예요."}
              </CustomTypography>
            </Stack>

            {loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: "80px" }}
              >
                <CircularProgress color="neutral" />
              </Box>
            ) : submitted ? (
              // 이미 제출된 계약 정보 — 수정 불가, 읽기 전용으로 보여준다
              <Stack spacing={"20px"} sx={{ mt: 4 }}>
                <InfoRow label="계약형태" value={contractTypeLabel} />
                {isBusiness ? (
                  <InfoRow label="사업자명" value={businessName || "-"} />
                ) : (
                  <InfoRow label="실명" value={realName || "-"} />
                )}
                <InfoRow
                  label="계약서를 받을 이메일"
                  value={contractEmail || "-"}
                />
                <InfoRow label="전화번호" value={contractPhoneNumber || "-"} />
                <InfoRow label="활동명" value={activityName || "-"} />
                {bankbookFile && (
                  <Stack spacing={"2px"}>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"통장사본"}
                    </CustomTypography>
                    <LinkV2
                      variant="Caption"
                      text="미리보기"
                      customSx={{
                        textDecoration: "underline",
                        width: "fit-content",
                      }}
                      onClick={() => openDataUrlPreview(bankbookFile)}
                    />
                  </Stack>
                )}
                {isBusiness && businessRegistrationFile && (
                  <Stack spacing={"2px"}>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"사업자등록증"}
                    </CustomTypography>
                    <LinkV2
                      variant="Caption"
                      text="미리보기"
                      customSx={{
                        textDecoration: "underline",
                        width: "fit-content",
                      }}
                      onClick={() =>
                        openDataUrlPreview(businessRegistrationFile)
                      }
                    />
                  </Stack>
                )}
                {/* 체결된 계약서 — 계약 완료 시 어드민이 업로드한 사본을 당사자가 확인·보관할 수 있다 */}
                {completed && contractFile && (
                  <Stack spacing={"2px"}>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"체결된 계약서"}
                    </CustomTypography>
                    <Stack direction="row" spacing={"12px"}>
                      <LinkV2
                        variant="Caption"
                        text="미리보기"
                        customSx={{
                          textDecoration: "underline",
                          width: "fit-content",
                        }}
                        onClick={() => openDataUrlPreview(contractFile)}
                      />
                      <LinkV2
                        variant="Caption"
                        text="다운로드"
                        customSx={{
                          textDecoration: "underline",
                          width: "fit-content",
                        }}
                        onClick={() =>
                          downloadDataUrl(
                            contractFile,
                            "디스턴싱-앰버서더-계약서"
                          )
                        }
                      />
                    </Stack>
                  </Stack>
                )}
                <CustomTypography
                  variant="Footnote"
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Weak,
                  }}
                >
                  {
                    "계약 정보는 제출 후 수정할 수 없어요. 변경이 필요하면 담당자에게 문의해 주세요."
                  }
                </CustomTypography>
              </Stack>
            ) : (
              <Stack spacing={4} sx={{ mt: 4 }}>
                <Stack spacing={"6px"}>
                  <FieldLabel text="계약형태" />
                  {/* 좁은 화면에서는 버튼 단위로 줄바꿈 — 버튼 안 글자는 줄바꿈 금지.
                      spacing(margin) 은 wrap 과 어긋나므로 gap 으로 간격을 준다 */}
                  <Stack direction={"row"} sx={{ flexWrap: "wrap", gap: 1 }}>
                    {CONTRACT_TYPES.map(({ value, label }) => (
                      <Button
                        key={value}
                        variant="SingleSlim"
                        size="md"
                        color={contractType === value ? "primary" : "secondary"}
                        sx={{
                          transition: "background-color 0.3s ease",
                          whiteSpace: "nowrap",
                          fontSize: "16px",
                        }}
                        onClick={() => setContractType(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>

                {isBusiness ? (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="계약서에 기재할 사업자명" />
                    <Input
                      sx={inputSx}
                      placeholder="사업자등록증상 상호"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                    />
                  </Stack>
                ) : (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="계약서에 기재할 실명" />
                    <Input
                      sx={inputSx}
                      placeholder="주민등록상 이름"
                      value={realName}
                      onChange={(e) => setRealName(e.target.value)}
                    />
                  </Stack>
                )}

                <FileField
                  label="통장사본"
                  value={bankbookFile}
                  fileName={bankbookFileName}
                  onChange={(dataUrl, name) => {
                    setBankbookFile(dataUrl);
                    setBankbookFileName(name);
                  }}
                  onError={setError}
                />

                {isBusiness && (
                  <FileField
                    label="사업자등록증"
                    value={businessRegistrationFile}
                    fileName={businessRegistrationFileName}
                    onChange={(dataUrl, name) => {
                      setBusinessRegistrationFile(dataUrl);
                      setBusinessRegistrationFileName(name);
                    }}
                    onError={setError}
                  />
                )}

                <Stack spacing={"6px"}>
                  <FieldLabel text="계약서를 받을 이메일" />
                  <Input
                    sx={inputSx}
                    type="email"
                    slotProps={{
                      input: { inputMode: "email", autoComplete: "email" },
                    }}
                    placeholder="email@example.com"
                    value={contractEmail}
                    onChange={(e) => setContractEmail(e.target.value)}
                  />
                </Stack>

                {isBusiness ? (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="연락 가능한 전화번호" />
                    <Input
                      sx={inputSx}
                      type="tel"
                      slotProps={{ input: { inputMode: "tel" } }}
                      placeholder="02-1234-5678 또는 010-1234-5678"
                      value={contractPhoneNumber}
                      onChange={(e) =>
                        setContractPhoneNumber(
                          e.target.value.replace(/[^\d-]/g, "")
                        )
                      }
                    />
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {
                        "계약·정산 관련 연락을 받을 번호예요. 유선 번호도 괜찮아요."
                      }
                    </CustomTypography>
                  </Stack>
                ) : (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="핸드폰 번호" />
                    {/* 로그인 계정의 인증된 번호 고정 — 폼에서 수정 불가 */}
                    <Input
                      sx={inputSx}
                      type="tel"
                      value={loginPhoneNumber}
                      disabled
                    />
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"가입 시 인증한 전화번호가 사용돼요."}
                    </CustomTypography>
                  </Stack>
                )}

                <Stack spacing={"6px"}>
                  <FieldLabel text="활동명" />
                  <Input
                    sx={inputSx}
                    slotProps={{ input: { maxLength: 11 } }}
                    placeholder="앰버서더 신청 시 기입한 활동명 (12자 미만)"
                    value={activityName}
                    onChange={(e) => setActivityName(e.target.value)}
                  />
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {"신청 내역과 대조해 확인하는 용도예요."}
                  </CustomTypography>
                </Stack>

                {error && (
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.HighlightWithAlert,
                    }}
                  >
                    {error}
                  </CustomTypography>
                )}

                <Button
                  variant="Action"
                  size="lg"
                  disabled={!canSubmit}
                  onClick={() => setConfirming(true)}
                  sx={{
                    width: "100%",
                    height: "54px",
                    fontWeight: 700,
                    borderRadius: "12px",
                    fontSize: "16px",
                  }}
                >
                  {"제출하기"}
                </Button>
              </Stack>
            )}

            {/* 제출 전 확인 팝업 — 제출 후에는 수정할 수 없으므로 입력 내용을 한 번 더 확인시킨다 */}
            {confirming && (
              <CustomModal
                open
                onClose={() => setConfirming(false)}
                title="제출 전에 확인해 주세요"
                subtitle="계약 정보는 제출 후 수정할 수 없어요."
                actions={[
                  {
                    label: "제출하기",
                    onClick: handleSubmit,
                    variant: "Solid",
                    buttonProps: { loading: submitting },
                  },
                  {
                    label: "취소",
                    onClick: () => setConfirming(false),
                    variant: "Outlined",
                  },
                ]}
              >
                <Stack spacing={"12px"}>
                  <InfoRow label="계약형태" value={contractTypeLabel} />
                  {isBusiness ? (
                    <InfoRow label="사업자명" value={businessName || "-"} />
                  ) : (
                    <InfoRow label="실명" value={realName || "-"} />
                  )}
                  <InfoRow
                    label="계약서를 받을 이메일"
                    value={contractEmail || "-"}
                  />
                  <InfoRow
                    label="전화번호"
                    value={
                      (isBusiness ? contractPhoneNumber : loginPhoneNumber) ||
                      "-"
                    }
                  />
                  <InfoRow label="활동명" value={activityName || "-"} />
                  <InfoRow
                    label="통장사본"
                    value={bankbookFileName ?? (bankbookFile ? "첨부됨" : "-")}
                  />
                  {isBusiness && (
                    <InfoRow
                      label="사업자등록증"
                      value={
                        businessRegistrationFileName ??
                        (businessRegistrationFile ? "첨부됨" : "-")
                      }
                    />
                  )}
                </Stack>
              </CustomModal>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// 읽기 전용 표시·제출 확인 팝업 공용 라벨/값 행
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={"2px"}>
      <CustomTypography
        variant="Footnote"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="Body"
        sx={{
          fontWeight: 500,
          color: theme.vars.palette.Content.Strong,
          wordBreak: "break-all",
        }}
      >
        {value}
      </CustomTypography>
    </Stack>
  );
}

function FieldLabel({ text }: { text: string }) {
  return (
    <CustomTypography
      variant="Caption"
      sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
    >
      {text}
    </CustomTypography>
  );
}

// data URL 을 파일로 다운로드 — MIME 으로 확장자를 정한다 (예: application/pdf → pdf).
function downloadDataUrl(dataUrl: string, baseName: string) {
  const mime = dataUrl.slice(5, dataUrl.indexOf(";"));
  const ext = mime === "application/pdf" ? "pdf" : mime.split("/")[1] || "bin";
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = `${baseName}.${ext}`;
  anchor.click();
}

// data URL 을 새 탭에서 미리보기 — 브라우저가 data: URL 의 최상위 탐색을 막으므로 Blob URL 로 변환해 연다.
function openDataUrlPreview(dataUrl: string) {
  const [meta, base64] = dataUrl.split(",");
  const mime =
    meta.match(/^data:(.*?);base64$/)?.[1] ?? "application/octet-stream";
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: mime }));
  window.open(url);
  // 즉시 revoke 하면 새 탭이 로드하기 전에 무효화된다 — 여유를 두고 해제
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// 파일 업로드 필드 — 이미지/PDF 를 base64 data URL 로 읽어 상태에 보관. 클릭 선택 외에 드래그앤드롭도 지원.
// 파일명 표시(기존 제출분은 원본명이 없어 "저장된 파일" 폴백), 이미지는 섬네일, 미리보기는 새 탭으로 연다.
function FileField({
  label,
  value,
  fileName,
  onChange,
  onError,
}: {
  label: string;
  value: string | null;
  fileName: string | null;
  onChange: (dataUrl: string | null, fileName: string | null) => void;
  onError: (message: string | null) => void;
}) {
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    // 드롭된 파일은 input accept 필터를 안 거친다 — 클릭·드롭 공통으로 형식을 검사한다
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      onError("이미지 또는 PDF 파일만 올릴 수 있어요.");
      return;
    }
    if (file.size > CONTRACT_FILE_MAX_BYTES) {
      onError("파일 크기는 5MB 이하로 올려 주세요.");
      return;
    }
    try {
      onChange(await readFileAsDataUrl(file), file.name);
      onError(null);
    } catch (e) {
      onError(FALLBACK_ERROR_MESSAGE);
    }
  };

  return (
    <Stack
      spacing={"6px"}
      // 필드 전체가 드롭 영역 — 드래그 중에는 점선 외곽선으로 표시한다 (outline 이라 레이아웃 이동 없음)
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        // 자식 요소로 이동할 때 발생하는 leave 는 무시한다 (깜빡임 방지)
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      sx={{
        borderRadius: "12px",
        outline: dragging
          ? `2px dashed ${theme.vars.palette.Content.Highlight}`
          : "none",
        outlineOffset: "6px",
      }}
    >
      <FieldLabel text={label} />
      {value && value.startsWith("data:image") && (
        <Box
          component="img"
          src={value}
          alt={label}
          onClick={() => openDataUrlPreview(value)}
          sx={{
            maxHeight: "160px",
            width: "fit-content",
            maxWidth: "100%",
            borderRadius: "12px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            objectFit: "contain",
            backgroundColor: theme.vars.palette.Surface.Forward,
            cursor: "zoom-in",
          }}
        />
      )}
      {value && (
        <Stack direction="row" alignItems="center" spacing={"8px"}>
          {/* 파일명 자체가 미리보기 링크 */}
          <CustomTypography
            variant="Caption"
            onClick={() => openDataUrlPreview(value)}
            sx={{
              fontWeight: 500,
              color: theme.vars.palette.Content.Medium,
              wordBreak: "break-all",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {fileName ?? "저장된 파일"}
          </CustomTypography>
          {/* 삭제 — X 아이콘 */}
          <CloseRoundedIcon
            titleAccess="파일 삭제"
            onClick={() => onChange(null, null)}
            sx={{
              flexShrink: 0,
              fontSize: "16px",
              color: theme.vars.palette.Content.Weak,
              cursor: "pointer",
            }}
          />
        </Stack>
      )}
      <Button
        component="label"
        variant="Outlined"
        size="sm"
        sx={{ width: "fit-content", fontWeight: 700 }}
      >
        {value ? "다시 올리기" : "파일 올리기"}
        <input
          hidden
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </Button>
      <CustomTypography
        variant="Footnote"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.SuperWeak }}
      >
        {"이미지 또는 PDF, 5MB 이하"}
      </CustomTypography>
    </Stack>
  );
}
